import { FC, useState } from 'react'
import {
  Box,
  Button,
  Card,
  // CardActions,
  CardContent,
  CardMedia,
  Grid,
  H3,
  H5,
  IconButton,
  Link,
  makeStyles,
  PageTitle,
} from '@perk-ui/core'
import { ArrowLeft } from 'react-feather'

import { ReactComponent as TeamIcon } from '../../assets/TeamIcon.svg'
import DataPair from '../../components/DataPair'
import { useSitesContext } from '../../components/SitesProvider'
import TableauView from '../../components/TableauView'
import config from '../../config/app-config'
// import { accessWorkbooks } from '../../config/constants'
import { useUser } from '../../features/auth/AuthContext'
import { Team } from '../../features/query-hooks/useProviders'
// import { keyToLabel } from '../Home/components/BodyPartGraph'

export interface MyTeamsProps {}

const useStyles = makeStyles((theme) => ({
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  emptyStateIcon: {
    display: 'flex',
    backgroundColor: '#F1F1F1',
    borderRadius: '50%',
    marginBottom: theme.spacing(2),
    '& svg': {
      color: '#6B6B6B',
    },
  },
  emptyStateTitle: {
    color: '#6B6B6B',
    fontWeight: 700,
    fontSize: '16px',
    marginBottom: theme.spacing(1),
  },
  emptyStateText: {
    color: '#6B6B6B',
    fontWeight: 400,
    fontSize: '12px',
    marginBottom: theme.spacing(2),
  },
  emptyStateButton: {
    fontSize: '14px',
    fontWeight: 400,
    marginBottom: theme.spacing(2),
    minHeight: 40,
    padding: theme.spacing(1, 3),
  },
  teamCard: {
    display: 'flex',
    padding: theme.spacing(2),
    cursor: 'pointer',
    border: 'solid 1px transparent',
    height: '100%',
    '&:hover': {
      opacity: '0.8',
      border: 'solid 1px #b3b3b3',
    },
  },
  teamCardMedia: {
    // backgroundImage: 'url(' + process.env.PUBLIC_URL + '/assets/card-gradient.jpg)',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    borderRadius: '12px',
    padding: theme.spacing(0, 2),
    // marginBottom: 'auto',
    objectFit: 'contain',
    objectPosition: 'top',
    width: '30%',
    height: '150px',
  },
  teamCardContent: {
    width: '70%',
    padding: theme.spacing(0, 3),
    '& h3': {
      marginBottom: theme.spacing(1),
    },
    '& span': {
      fontWeight: 700,
    },
  },
  teamCardActions: {
    padding: 0,
    '& button': {
      padding: theme.spacing(1, 2),
      borderRadius: '16px',
    },
  },
}))

const MyTeams: FC<MyTeamsProps> = () => {
  const sitesContext = useSitesContext()
  const [activeTeam, setActiveTeam] = useState<Team | null>(null)
  const currentUser = useUser()
  const classes = useStyles()
  const site = sitesContext?.currentSite
  // const staffTableauSubscription = currentUser?.staff.subscriptionPlan
  const providerTeams = currentUser?.staff?.teams || []
  const pageTitle = 'My Teams'

  if (!site) return null

  const emptyStatePage = () => {
    const icon = <TeamIcon />
    const title = 'No Teams'
    const pageText = 'You are currently not part of any Teams'
    const buttonText = 'Learn more about Teams'
    const buttonLink = 'https://www.databiologics.com/teams'

    return (
      <Box p={3} height={'100%'}>
        <Box className={classes.emptyContainer}>
          <Box className={classes.emptyStateIcon} p={1}>
            {icon}
          </Box>
          <H3 className={classes.emptyStateTitle}>{title}</H3>
          <H5 className={classes.emptyStateText}>{pageText}</H5>
          <Link
            component={Button}
            className={classes.emptyStateButton}
            href={buttonLink}
            target="_blank"
          >
            {buttonText}
          </Link>
        </Box>
      </Box>
    )
  }
  const getCaptainsNames = (team: Team) => {
    return (team?.teamStaffs || [])
      .filter((team) => team.role === 'Captain')
      .map((captain, index) => {
        if (index > 1) return ''
        return `${index === 1 ? ' & ' : ''}${captain?.staff?.firstName} ${
          captain?.staff?.lastName
        }${captain?.staff?.title ? ', ' + captain?.staff?.title : ''}`
      })
      .join('')
  }
  const getWorkbookUrl = () => {
    switch (activeTeam?.level) {
      case 'standard':
        return 'views/team_standard/StandardTeamHome'
      case 'premium':
        return 'views/team_premium/PremiumTeamHome'
      default:
        return ''
    }
  }

  const myTeamsTableauParameters = [
    {
      key: 'parameter_select_subscription_type',
      value: 'TEAM',
    },
    // {
    //   key: 'parameter_select_team_id',
    //   value: activeTeam?.id,
    // },
    // {
    //   key: 'parameter_select_provider_id',
    //   value: currentUser?.staff?.id,
    // },
  ]

  const filteredProviderTeams = providerTeams.filter((team) =>
    team.teamStaffs?.find(
      (teamSt) =>
        teamSt.teamId == team.id &&
        teamSt.status == 'active' &&
        team.status == 'active',
    ),
  )
  return (
    <Box>
      <PageTitle title={pageTitle} />
      {filteredProviderTeams.length == 0 && emptyStatePage()}
      {filteredProviderTeams.length > 0 && !activeTeam && (
        <Grid container spacing={2} p={3}>
          {filteredProviderTeams.map((team) => (
            <Grid item xs={12} sm={6} key={team.id}>
              <Card
                key={team.id}
                className={classes.teamCard}
                onClick={() => setActiveTeam(team)}
              >
                {/* <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                  <Box> */}
                <CardMedia
                  component="img"
                  className={classes.teamCardMedia}
                  sx={{ width: 151 }}
                  image={
                    team.badgeUrl
                      ? config.cdnPath + '/' + team.badgeUrl
                      : process.env.PUBLIC_URL + '/assets/default-dbio.png'
                  }
                  alt={team.name}
                />
                <CardContent className={classes.teamCardContent}>
                  <H3>{team.name} Team</H3>
                  <DataPair label="Captains" value={getCaptainsNames(team)} />
                  {/* <DataPair label="Level" value={keyToLabel(team.level)} /> */}
                  <DataPair label="Sponsor" value={team.sponsor?.name} />
                  <DataPair label="Description" value={team.description} />
                </CardContent>
                {/* </Box>
                  <Box>
                    <CardActions className={classes.teamCardActions}>
                      <Button
                        endIcon={<ArrowRight />}
                        fullWidth
                        size="small"
                        onClick={() => setActiveTeam(team)}
                      >
                        View
                      </Button>
                    </CardActions>
                  </Box>
                </Box> */}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {activeTeam && (
        <Box height={'100%'}>
          <Box display="flex" alignItems={'center'} p={2}>
            <IconButton onClick={() => setActiveTeam(null)}>
              <ArrowLeft />
            </IconButton>
            <H3>{activeTeam.name} Team</H3>
          </Box>
          <TableauView
            src={getWorkbookUrl()}
            toolbar="top"
            hideTabs={false}
            parameters={myTeamsTableauParameters}
            section="my-teams"
            id={activeTeam.id}
          />
        </Box>
      )}
    </Box>
  )
}

export default MyTeams
